import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp, arrayUnion, arrayRemove, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

export interface ConnectionRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  status: 'pending' | 'accepted' | 'declined' | 'withdrawn';
  matchScore: number;
  matchReasons: string[];
  sharedInterests: string[];
  industryOverlap: string[];
  learningGoals: string[];
  message?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  respondedAt?: Timestamp;
  knownContact?: {
    markedBy: string;
    markedAt: Timestamp;
    status: 'pending' | 'direct_connect' | 'declined' | 'continue';
  };
}

export interface EntrepreneurProfile {
  uid: string;
  name: string;
  company: string;
  industry: string[];
  /**
   * Branches of sectoren waar deze ondernemer klanten wil vinden.
   * Dit is het primaire veld voor cross-match scoring: iemand anders
   * wiens industry overlapt met jouw targetClientIndustry is een potentiële match.
   */
  targetClientIndustry?: string[];
  /**
   * Concrete diensten of producten die deze ondernemer aanbiedt.
   * Gebruik voor context in match reasons en profielweergave.
   */
  servicesOffered?: string[];
  interests: string[];
  learningGoals: string[];
  skills: string[];
  experience: string;
  isOpenForConnections: boolean;
  preferredConnectionTypes: string[];
  location: string;
  bio: string;
  profileImage?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ConnectionMatch {
  userId: string;
  matchScore: number;
  matchReasons: string[];
  sharedInterests: string[];
  industryOverlap: string[];
  learningGoals: string[];
  profile: EntrepreneurProfile;
}

class ConnectionRequestService {
  private readonly CONNECTION_REQUESTS_COLLECTION = 'connectionRequests';
  private readonly ENTREPRENEUR_PROFILES_COLLECTION = 'entrepreneurProfiles';

  /**
   * Create or update entrepreneur profile
   */
  async updateEntrepreneurProfile(uid: string, profile: Partial<EntrepreneurProfile>): Promise<void> {
    try {
      const profileRef = doc(db, this.ENTREPRENEUR_PROFILES_COLLECTION, uid);
      await updateDoc(profileRef, {
        ...profile,
        updatedAt: Timestamp.now()
      });
    } catch (error: any) {
      // If profile doesn't exist, create it
      if (error.code === 'not-found') {
        await addDoc(collection(db, this.ENTREPRENEUR_PROFILES_COLLECTION), {
          uid,
          ...profile,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      } else {
        throw error;
      }
    }
  }

  /**
   * Get entrepreneur profile
   */
  async getEntrepreneurProfile(uid: string): Promise<EntrepreneurProfile | null> {
    try {
      const profileQuery = query(
        collection(db, this.ENTREPRENEUR_PROFILES_COLLECTION),
        where('uid', '==', uid)
      );
      const profileDoc = await getDocs(profileQuery);
      
      if (profileDoc.docs.length > 0) {
        return profileDoc.docs[0].data() as EntrepreneurProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting entrepreneur profile:', error);
      return null;
    }
  }

  /**
   * Find potential connection matches based on shared interests, industry overlap, and learning goals
   */
  async findConnectionMatches(userId: string, limit: number = 10): Promise<ConnectionMatch[]> {
    try {
      const userProfile = await this.getEntrepreneurProfile(userId);
      if (!userProfile || !userProfile.isOpenForConnections) {
        throw new Error('User profile not found or not open for connections');
      }

      // Get all other entrepreneur profiles
      const allProfiles = await this.getAllEntrepreneurProfiles(userId);
      const openProfiles = allProfiles.filter(p => p.isOpenForConnections);

      // Calculate match scores for each profile
      const potentialMatches = openProfiles.map(profile => ({
        userId: profile.uid,
        matchScore: this.calculateConnectionMatchScore(userProfile, profile),
        matchReasons: this.generateConnectionMatchReasons(userProfile, profile),
        sharedInterests: this.findSharedInterests(userProfile, profile),
        industryOverlap: this.findIndustryOverlap(userProfile, profile),
        learningGoals: this.findLearningGoals(userProfile, profile),
        profile
      }));

      // Sort by match score and take top matches
      const topMatches = potentialMatches
        .sort((a, b) => b.matchScore - a.matchScore)
        .slice(0, limit);

      return topMatches;
    } catch (error) {
      console.error('Error finding connection matches:', error);
      throw error;
    }
  }

  /**
   * Send connection request with one click
   */
  async sendConnectionRequest(fromUserId: string, toUserId: string, message?: string, isKnownContact?: boolean): Promise<ConnectionRequest> {
    try {
      // Check if request already exists
      const existingRequest = await this.getExistingConnectionRequest(fromUserId, toUserId);
      if (existingRequest) {
        throw new Error('Connection request already exists');
      }

      // Get both profiles to calculate match score (optional - works without entrepreneur profiles)
      const [fromProfile, toProfile] = await Promise.all([
        this.getEntrepreneurProfile(fromUserId).catch(() => null),
        this.getEntrepreneurProfile(toUserId).catch(() => null)
      ]);

      // Calculate match data if profiles exist, otherwise use defaults
      const matchScore = (fromProfile && toProfile) ? this.calculateConnectionMatchScore(fromProfile, toProfile) : 50;
      const matchReasons = (fromProfile && toProfile) ? this.generateConnectionMatchReasons(fromProfile, toProfile) : ['Direct connection request'];
      const sharedInterests = (fromProfile && toProfile) ? this.findSharedInterests(fromProfile, toProfile) : [];
      const industryOverlap = (fromProfile && toProfile) ? this.findIndustryOverlap(fromProfile, toProfile) : [];
      const learningGoals = (fromProfile && toProfile) ? this.findLearningGoals(fromProfile, toProfile) : [];

      const connectionRequest: Omit<ConnectionRequest, 'id'> = {
        fromUserId,
        toUserId,
        status: 'pending',
        matchScore,
        matchReasons,
        sharedInterests,
        industryOverlap,
        learningGoals,
        message,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        ...(isKnownContact && {
          knownContact: {
            markedBy: fromUserId,
            markedAt: Timestamp.now(),
            status: 'pending'
          }
        })
      };

      const docRef = await addDoc(collection(db, this.CONNECTION_REQUESTS_COLLECTION), connectionRequest);
      
      return {
        id: docRef.id,
        ...connectionRequest
      };
    } catch (error) {
      console.error('Error sending connection request:', error);
      throw error;
    }
  }

  /**
   * Accept connection request
   */
  async acceptConnectionRequest(requestId: string): Promise<void> {
    try {
      const requestRef = doc(db, this.CONNECTION_REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        status: 'accepted',
        respondedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error accepting connection request:', error);
      throw error;
    }
  }

  /**
   * Handle known contact response: direct connect, verify call, decline, or continue
   */
  async handleKnownContactResponse(
    requestId: string, 
    response: 'direct_connect' | 'verify_call' | 'declined' | 'continue'
  ): Promise<void> {
    try {
      const requestRef = doc(db, this.CONNECTION_REQUESTS_COLLECTION, requestId);
      
      if (response === 'direct_connect') {
        // Direct connect: accept request and mark knownContact as direct_connect
        await updateDoc(requestRef, {
          status: 'accepted',
          'knownContact.status': 'direct_connect',
          respondedAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      } else if (response === 'verify_call') {
        // Verify call: accept request but require verification call before full connection
        await updateDoc(requestRef, {
          status: 'accepted',
          'knownContact.status': 'verify_call',
          'knownContact.verificationOnly': true,
          'knownContact.requestedMode': 'verify_call',
          respondedAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      } else if (response === 'declined') {
        // Decline: mark as declined
        await updateDoc(requestRef, {
          status: 'declined',
          'knownContact.status': 'declined',
          respondedAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      } else {
        // Continue: mark as continue, keep status pending for normal flow
        await updateDoc(requestRef, {
          'knownContact.status': 'continue',
          updatedAt: Timestamp.now()
        });
      }
    } catch (error) {
      console.error('Error handling known contact response:', error);
      throw error;
    }
  }

  /**
   * Decline connection request
   */
  async declineConnectionRequest(requestId: string): Promise<void> {
    try {
      const requestRef = doc(db, this.CONNECTION_REQUESTS_COLLECTION, requestId);
      await updateDoc(requestRef, {
        status: 'declined',
        respondedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error declining connection request:', error);
      throw error;
    }
  }

  /**
   * Get received connection requests
   */
  async getReceivedConnectionRequests(userId: string): Promise<ConnectionRequest[]> {
    try {
      const requestsQuery = query(
        collection(db, this.CONNECTION_REQUESTS_COLLECTION),
        where('toUserId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const requestsDoc = await getDocs(requestsQuery);
      
      return requestsDoc.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ConnectionRequest[];
    } catch (error) {
      console.error('Error getting received connection requests:', error);
      return [];
    }
  }

  /**
   * Get sent connection requests
   */
  async getSentConnectionRequests(userId: string): Promise<ConnectionRequest[]> {
    try {
      const requestsQuery = query(
        collection(db, this.CONNECTION_REQUESTS_COLLECTION),
        where('fromUserId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const requestsDoc = await getDocs(requestsQuery);
      
      return requestsDoc.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ConnectionRequest[];
    } catch (error) {
      console.error('Error getting sent connection requests:', error);
      return [];
    }
  }

  /**
   * Calculate connection match score (0-100).
   *
   * Scoring philosophy: ondernemers zoeken potentiële klanten, geen vakgenoten.
   * Zelfde branche = concurrent, niet een lead. Cross-match op targetClientIndustry
   * is het sterkste signaal; aangevuld met skills-complementariteit en regio.
   *
   * Gewichten:
   *   35% — Cross-client match (A.industry ∩ B.targetClientIndustry of vice versa)
   *   25% — Skills complementariteit (leverancier / afnemer relatie)
   *   20% — Regio overlap (lokale zakelijke kansen)
   *   10% — Gedeelde interesses (soft context)
   *   10% — Leerdoelen (secundair signaal)
   */
  private calculateConnectionMatchScore(profile1: EntrepreneurProfile, profile2: EntrepreneurProfile): number {
    let score = 0;
    let totalWeight = 0;

    // Cross-client match (weight: 35%)
    // Hoog als jouw branche matcht met wat de ander zoekt als klant, of andersom
    const crossWeight = 35;
    const crossScore = this.calculateCrossClientMatchScore(profile1, profile2);
    score += crossScore * crossWeight;
    totalWeight += crossWeight;

    // Skills complementarity (weight: 25%)
    const skillsWeight = 25;
    const skillsScore = this.calculateSkillsComplementarity(profile1.skills, profile2.skills);
    score += skillsScore * skillsWeight;
    totalWeight += skillsWeight;

    // Region overlap (weight: 20%)
    const regionWeight = 20;
    const regionScore = this.calculateRegionOverlap(profile1.location, profile2.location);
    score += regionScore * regionWeight;
    totalWeight += regionWeight;

    // Shared interests (weight: 10%)
    const interestsWeight = 10;
    const interestsScore = this.calculateInterestsOverlap(profile1.interests, profile2.interests);
    score += interestsScore * interestsWeight;
    totalWeight += interestsWeight;

    // Learning goals (weight: 10%)
    const goalsWeight = 10;
    const goalsScore = this.calculateLearningGoalsAlignment(profile1.learningGoals, profile2.learningGoals);
    score += goalsScore * goalsWeight;
    totalWeight += goalsWeight;

    return Math.round(score / totalWeight);
  }

  /**
   * Cross-client match: hoe goed sluit A's branche aan bij wat B zoekt als klant,
   * en vice versa? Geeft een gecombineerde score terug (0-100).
   */
  private calculateCrossClientMatchScore(profile1: EntrepreneurProfile, profile2: EntrepreneurProfile): number {
    const normalize = (arr: string[]) => arr.map(s => s.toLowerCase().trim());

    const industry1 = normalize(profile1.industry);
    const industry2 = normalize(profile2.industry);
    const target1 = normalize(profile1.targetClientIndustry ?? []);
    const target2 = normalize(profile2.targetClientIndustry ?? []);

    if (target1.length === 0 && target2.length === 0) {
      // Geen target-data: val terug op neutrale score om geen valse hoge match te creëren
      return 30;
    }

    // A zoekt klanten in B's branche
    const aWantsB = target1.length > 0 && industry2.length > 0
      ? industry2.filter(i => target1.includes(i)).length / Math.max(target1.length, industry2.length) * 100
      : 0;

    // B zoekt klanten in A's branche
    const bWantsA = target2.length > 0 && industry1.length > 0
      ? industry1.filter(i => target2.includes(i)).length / Math.max(target2.length, industry1.length) * 100
      : 0;

    return Math.max(aWantsB, bWantsA);
  }

  /**
   * Regio overlap: check of de location strings een gemeenschappelijke provincie/stad bevatten.
   * Ruwe string-matching; voldoende voor een gewogen signaal.
   */
  private calculateRegionOverlap(location1: string, location2: string): number {
    if (!location1 || !location2) return 20; // neutraal als geen data
    const l1 = location1.toLowerCase();
    const l2 = location2.toLowerCase();
    if (l1 === l2) return 100;
    // Gemeenschappelijke tokens (woorden)
    const tokens1 = l1.split(/[\s,]+/).filter(t => t.length > 2);
    const tokens2 = l2.split(/[\s,]+/).filter(t => t.length > 2);
    const shared = tokens1.filter(t => tokens2.includes(t));
    if (shared.length > 0) return 70;
    return 0;
  }

  /**
   * Calculate interests overlap score
   */
  private calculateInterestsOverlap(interests1: string[], interests2: string[]): number {
    if (interests1.length === 0 || interests2.length === 0) return 0;

    const normalized1 = interests1.map(i => i.toLowerCase().trim());
    const normalized2 = interests2.map(i => i.toLowerCase().trim());

    const intersection = normalized1.filter(interest => normalized2.includes(interest));
    const union = Array.from(new Set([...normalized1, ...normalized2]));

    return (intersection.length / union.length) * 100;
  }

  /**
   * Calculate industry overlap score
   */
  private calculateIndustryOverlap(industry1: string[], industry2: string[]): number {
    if (industry1.length === 0 || industry2.length === 0) return 0;

    const normalized1 = industry1.map(i => i.toLowerCase().trim());
    const normalized2 = industry2.map(i => i.toLowerCase().trim());

    const intersection = normalized1.filter(industry => normalized2.includes(industry));
    const union = Array.from(new Set([...normalized1, ...normalized2]));

    return (intersection.length / union.length) * 100;
  }

  /**
   * Calculate learning goals alignment score
   */
  private calculateLearningGoalsAlignment(goals1: string[], goals2: string[]): number {
    if (goals1.length === 0 || goals2.length === 0) return 0;

    const normalized1 = goals1.map(g => g.toLowerCase().trim());
    const normalized2 = goals2.map(g => g.toLowerCase().trim());

    const intersection = normalized1.filter(goal => normalized2.includes(goal));
    const union = Array.from(new Set([...normalized1, ...normalized2]));

    return (intersection.length / union.length) * 100;
  }

  /**
   * Calculate skills complementarity score
   */
  private calculateSkillsComplementarity(skills1: string[], skills2: string[]): number {
    if (skills1.length === 0 || skills2.length === 0) return 50; // Neutral score

    const normalized1 = skills1.map(s => s.toLowerCase().trim());
    const normalized2 = skills2.map(s => s.toLowerCase().trim());

    // Check for complementary skills (different but related)
    const complementaryMatches = normalized1.filter(skill1 => 
      normalized2.some(skill2 => this.areSkillsComplementary(skill1, skill2))
    );

    const totalSkills = normalized1.length + normalized2.length;
    return (complementaryMatches.length / totalSkills) * 100;
  }

  /**
   * Check if two skills are complementary
   */
  private areSkillsComplementary(skill1: string, skill2: string): boolean {
    // Define complementary skill pairs
    const complementaryPairs = [
      ['marketing', 'sales'],
      ['design', 'development'],
      ['finance', 'operations'],
      ['strategy', 'execution'],
      ['leadership', 'management'],
      ['technical', 'business'],
      ['creative', 'analytical'],
      ['frontend', 'backend'],
      ['content', 'seo'],
      ['social media', 'pr']
    ];

    return complementaryPairs.some(pair => 
      (pair[0] === skill1 && pair[1] === skill2) ||
      (pair[1] === skill1 && pair[0] === skill2)
    );
  }

  /**
   * Generate human-readable match reasons focused on business value.
   *
   * Filosofie: toon zakelijke relevantie, geen technische overlap-labels.
   * Altijd één primaire reden; maximaal één secundaire reden.
   */
  private generateConnectionMatchReasons(profile1: EntrepreneurProfile, profile2: EntrepreneurProfile): string[] {
    const normalize = (arr: string[]) => arr.map(s => s.toLowerCase().trim());
    const reasons: string[] = [];

    const industry1 = normalize(profile1.industry);
    const industry2 = normalize(profile2.industry);
    const target1 = normalize(profile1.targetClientIndustry ?? []);
    const target2 = normalize(profile2.targetClientIndustry ?? []);

    // Primaire reden: cross-client match
    const aWantsB = industry2.some(i => target1.includes(i));
    const bWantsA = industry1.some(i => target2.includes(i));

    if (aWantsB && bWantsA) {
      reasons.push('Jullie zijn voor elkaar potentiële klant én leverancier.');
    } else if (aWantsB) {
      reasons.push(`Werkt in de sector waar jij graag klanten wilt.`);
    } else if (bWantsA) {
      reasons.push(`Zoekt klanten in jouw branche.`);
    } else {
      // Fallback primaire reden: beiden bouwen aan eigen doelstellingen
      reasons.push('Net als jij bezig met het bouwen van eigen doelstellingen.');
    }

    // Secundaire reden: regio (alleen als ook inhoudelijk relevant)
    const regionScore = this.calculateRegionOverlap(profile1.location, profile2.location);
    if (regionScore >= 70 && reasons.length < 2) {
      reasons.push('Actief in dezelfde regio.');
    }

    // Secundaire reden: complementaire vaardigheden
    const complementarySkills = this.findComplementarySkills(profile1, profile2);
    if (complementarySkills.length > 0 && reasons.length < 2) {
      reasons.push('Complementaire vaardigheden.');
    }

    return reasons.slice(0, 2);
  }

  /**
   * Find shared interests between two profiles
   */
  private findSharedInterests(profile1: EntrepreneurProfile, profile2: EntrepreneurProfile): string[] {
    const normalized1 = profile1.interests.map(i => i.toLowerCase().trim());
    const normalized2 = profile2.interests.map(i => i.toLowerCase().trim());
    
    return normalized1.filter(interest => normalized2.includes(interest));
  }

  /**
   * Find industry overlap between two profiles
   */
  private findIndustryOverlap(profile1: EntrepreneurProfile, profile2: EntrepreneurProfile): string[] {
    const normalized1 = profile1.industry.map(i => i.toLowerCase().trim());
    const normalized2 = profile2.industry.map(i => i.toLowerCase().trim());
    
    return normalized1.filter(industry => normalized2.includes(industry));
  }

  /**
   * Find learning goals between two profiles
   */
  private findLearningGoals(profile1: EntrepreneurProfile, profile2: EntrepreneurProfile): string[] {
    const normalized1 = profile1.learningGoals.map(g => g.toLowerCase().trim());
    const normalized2 = profile2.learningGoals.map(g => g.toLowerCase().trim());
    
    return normalized1.filter(goal => normalized2.includes(goal));
  }

  /**
   * Find complementary skills between two profiles
   */
  private findComplementarySkills(profile1: EntrepreneurProfile, profile2: EntrepreneurProfile): string[] {
    const normalized1 = profile1.skills.map(s => s.toLowerCase().trim());
    const normalized2 = profile2.skills.map(s => s.toLowerCase().trim());
    
    return normalized1.filter(skill1 => 
      normalized2.some(skill2 => this.areSkillsComplementary(skill1, skill2))
    );
  }

  /**
   * Get all entrepreneur profiles except the current user
   */
  private async getAllEntrepreneurProfiles(excludeUserId: string): Promise<EntrepreneurProfile[]> {
    try {
      const profilesQuery = query(
        collection(db, this.ENTREPRENEUR_PROFILES_COLLECTION),
        where('uid', '!=', excludeUserId)
      );
      const profilesDoc = await getDocs(profilesQuery);
      
      return profilesDoc.docs.map(doc => doc.data() as EntrepreneurProfile);
    } catch (error) {
      console.error('Error getting all entrepreneur profiles:', error);
      return [];
    }
  }

  /**
   * Check if connection request already exists
   */
  private async getExistingConnectionRequest(fromUserId: string, toUserId: string): Promise<ConnectionRequest | null> {
    try {
      const requestQuery = query(
        collection(db, this.CONNECTION_REQUESTS_COLLECTION),
        where('fromUserId', '==', fromUserId),
        where('toUserId', '==', toUserId)
      );
      const requestDoc = await getDocs(requestQuery);
      
      if (requestDoc.docs.length > 0) {
        return {
          id: requestDoc.docs[0].id,
          ...requestDoc.docs[0].data()
        } as ConnectionRequest;
      }
      return null;
    } catch (error) {
      console.error('Error checking existing connection request:', error);
      return null;
    }
  }
}

export const connectionRequestService = new ConnectionRequestService();

